import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSnackbar } from 'notistack';
import { catalogosService } from '../../services/catalogosService';
import { getApiErrorMessage } from '../../utils/apiError';

export function nombreArchivoRdlcCorto(ruta: string | undefined | null): string {
  if (!ruta) return '';
  let valor = ruta.trim();
  const upper = valor.toUpperCase();
  if (upper.startsWith('RDLC|') || upper.startsWith('CRYSTAL|')) {
    valor = valor.slice(valor.indexOf('|') + 1).trim();
  }
  const sep = valor.includes('/') ? '/' : '\\';
  return valor.split(sep).pop() ?? valor;
}

export interface ArchivoCodigoBarrasCardProps {
  titulo: string;
  descripcion: string;
  rutaRdlc: string;
  onRutaChange: (ruta: string) => void | Promise<void>;
  persistHint?: string;
}

const ArchivoCodigoBarrasCard: React.FC<ArchivoCodigoBarrasCardProps> = ({
  titulo,
  descripcion,
  rutaRdlc,
  onRutaChange,
  persistHint,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleUploadRdlc = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (ext !== '.rdlc' && ext !== '.rdl') {
      enqueueSnackbar('Solo se permiten archivos .rdlc o .rdl', { variant: 'warning' });
      return;
    }

    try {
      setUploading(true);
      const result = await catalogosService.uploadRdlcEtiquetas(file);
      const ruta = (result.rutaRdlc || '').trim();
      if (!ruta) {
        enqueueSnackbar('El servidor no devolvió la ruta del archivo.', { variant: 'error' });
        return;
      }
      await onRutaChange(ruta);
      enqueueSnackbar(persistHint || 'Archivo .rdlc de etiquetas subido.', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al subir el archivo .rdlc'), { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadRdlc = async () => {
    const ruta = (rutaRdlc || '').trim();
    if (!ruta) {
      enqueueSnackbar('No hay un archivo .rdlc configurado para descargar', { variant: 'warning' });
      return;
    }

    try {
      setDownloading(true);
      await catalogosService.downloadRdlcEtiquetas(ruta);
      enqueueSnackbar('Archivo .rdlc descargado', { variant: 'success' });
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al descargar el archivo .rdlc'), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {titulo}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {descripcion}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <Tooltip title={(rutaRdlc || '').trim() || 'Sin archivo seleccionado'}>
            <TextField
              label="Archivo .rdlc"
              value={nombreArchivoRdlcCorto(rutaRdlc)}
              fullWidth
              placeholder="Sin archivo seleccionado"
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Tooltip>
          <Tooltip title="Descargar .rdlc configurado">
            <span>
              <IconButton
                color="primary"
                onClick={() => void handleDownloadRdlc()}
                disabled={downloading || uploading || !(rutaRdlc || '').trim()}
                aria-label="Descargar archivo .rdlc configurado"
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  minHeight: 56,
                  minWidth: 56,
                }}
              >
                {downloading ? <CircularProgress size={20} /> : <FileDownloadOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading || downloading}
            aria-label="Seleccionar archivo .rdlc"
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              whiteSpace: 'nowrap',
              minHeight: 56,
              minWidth: 56,
              px: 1.5,
              fontSize: '1.25rem',
              lineHeight: 1,
            }}
          >
            {uploading ? <CircularProgress size={18} /> : '…'}
            <input
              type="file"
              accept=".rdlc,.rdl,application/xml,text/xml"
              hidden
              onChange={(e) => void handleUploadRdlc(e)}
            />
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ArchivoCodigoBarrasCard;
